import { searchHubwayzHotels } from "../../services/hubwayzHotelsApi";
import { hideSupplierBrandFromUserText } from "../unifiedSearchMockup/hideSupplierBrandFromUserText";
import { mapHubwayzToHotelSearchResults } from "./mapHubwayzToHotelSearchResults";
import { resolveHubwayzDestinationFromSearchPhrase } from "./resolveHubwayzDestinationFromSearchPhrase";
import {
  HUBWAYZ_HOTELS_TAB_SNAPSHOT_KEY,
  isHubwayzHotelsTabEnabled,
} from "./hubwayzHotelsTabConfig";
import { hubwayzCurrenciesForSelection } from "./hubwayzHotelLiveSearch";

function toIsoDate(raw) {
  const s = String(raw || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const dd = m[1].padStart(2, "0");
    const mm = m[2].padStart(2, "0");
    return `${m[3]}-${mm}-${dd}`;
  }
  return s;
}

/**
 * Normalize SearchContext / roomInfo room shapes into Hubwayz occupancy.
 *
 * SearchContext often sends `{ AdultsCount, KidsAges, InfantsCount }`
 * (see sortedRoomInfoJson). Using `r.adults || 2` wrongly turns
 * AdultsCount:1 into adults:2 and collapses multi-room 1-adult searches.
 */
export function roomsFromSearchPhrase(searchPhrase) {
  const rooms = Array.isArray(searchPhrase?.rooms) ? searchPhrase.rooms : null;
  if (rooms?.length) {
    return rooms.map((r) => {
      const adultsRaw =
        r?.adults ?? r?.AdultsCount ?? r?.Adults ?? r?.ADULTS;
      const adultsNum = Number(adultsRaw);
      const adults = Number.isFinite(adultsNum) && adultsNum >= 1 ? adultsNum : 1;

      const agesRaw =
        r?.childAges ??
        r?.KidsAges ??
        r?.kidsAges ??
        r?.children ??
        [];
      const childAges = Array.isArray(agesRaw)
        ? agesRaw.map((a) => Number(a)).filter((n) => Number.isFinite(n))
        : [];

      return { adults, childAges };
    });
  }
  return [{ adults: 2, childAges: [] }];
}

function saveSnapshot(snapshot) {
  try {
    sessionStorage.setItem(
      HUBWAYZ_HOTELS_TAB_SNAPSHOT_KEY,
      JSON.stringify(snapshot)
    );
  } catch {
    /* ignore quota */
  }
}

export function readHubwayzHotelsTabSnapshot() {
  try {
    const raw = sessionStorage.getItem(HUBWAYZ_HOTELS_TAB_SNAPSHOT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Drop-in replacement for searchHotels() when Hotels tab uses Hubwayz.
 * Returns axios-like `{ data: { session, Result }, status }` for SearchContext.
 */
export async function searchHotelsViaHubwayz(searchPhrase, location = null) {
  if (!isHubwayzHotelsTabEnabled()) {
    return {
      data: { error: "חיפוש המלונות אינו זמין" },
      status: 400,
      error: "disabled",
    };
  }

  let checkIn = toIsoDate(searchPhrase?.checkIn);
  let checkOut = toIsoDate(searchPhrase?.checkOut);
  // The compact bar shows the earlier day first. The supplier rejects a stay
  // whose check-in is after check-out and returns no hotels.
  if (
    /^\d{4}-\d{2}-\d{2}$/.test(checkIn) &&
    /^\d{4}-\d{2}-\d{2}$/.test(checkOut) &&
    checkIn > checkOut
  ) {
    const earlier = checkOut;
    checkOut = checkIn;
    checkIn = earlier;
  }
  if (!checkIn || !checkOut) {
    return {
      data: { error: "חסרים תאריכי צ׳ק־אין / צ׳ק־אאוט" },
      status: 400,
      error: "missing_dates",
    };
  }

  const dest = await resolveHubwayzDestinationFromSearchPhrase(
    searchPhrase,
    location
  );
  if (!dest.ok) {
    return {
      data: { error: dest.error || "destination_resolve_failed" },
      status: 400,
      error: dest.error,
    };
  }

  const rooms = roomsFromSearchPhrase(searchPhrase);
  const currencies = hubwayzCurrenciesForSelection(searchPhrase?.currency);
  if (!currencies) {
    return {
      data: { error: "missing_search_currency" },
      status: 400,
      error: "missing_search_currency",
    };
  }
  const currency = currencies[0];

  const res = await searchHubwayzHotels({
    destinationId: dest.destinationId,
    destinationType: dest.destinationType === "hotel" ? "hotel" : "location",
    countryCode: dest.countryCode,
    checkIn,
    checkOut,
    rooms,
    customerCountry: "IL",
    currencies,
    waitUntilDone: true,
    enrichStatic: true,
  });

  if (!res?.ok) {
    return {
      data: {
        error: hideSupplierBrandFromUserText(res?.error, "החיפוש נכשל"),
        upstream: res?.upstream ?? null,
      },
      status: res?.status || 502,
      error: hideSupplierBrandFromUserText(res?.error, "החיפוש נכשל"),
    };
  }

  const Result = mapHubwayzToHotelSearchResults(res.data || {}, {
    city: searchPhrase?.city,
    country: searchPhrase?.country,
    searchRequest: res.requestPayload || null,
    displayCurrency: currency,
    verifiedQuotes: res.verifiedQuotes || [],
    searchId: res.searchId || null,
  });

  const session = `hwz-${Date.now()}-${dest.destinationId}`;
  const snapshot = {
    session,
    destinationId: dest.destinationId,
    matchedName: dest.matchedName,
    countryCode: dest.countryCode,
    searchRequest: res.requestPayload || null,
    mishorData: res.data || null,
    searchPhrase: {
      checkIn: searchPhrase?.checkIn,
      checkOut: searchPhrase?.checkOut,
      city: searchPhrase?.city,
      country: searchPhrase?.country,
      currency,
      rooms,
    },
    checkInIso: checkIn,
    checkOutIso: checkOut,
    createdAt: new Date().toISOString(),
  };
  saveSnapshot(snapshot);

  return {
    data: {
      session,
      Result,
      provider: "hubwayz_mishor",
      hubwayz: {
        destinationId: dest.destinationId,
        matchedName: dest.matchedName,
        hotelsCount: Result.length,
      },
    },
    status: 200,
  };
}
